import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const EMPTY = {
  assigned: [], mediaServers: [], vicidial: [], pdServers: [], inboundGroups: [],
  userGroups: [], teamUsers: [], vendors: [], leadLists: [], timezones: [], templates: [],
  defaultOpensipsId: null, defaultMediaId: null, defaultVicidialId: null,
};

/** Option sources shared by the campaign create dialog and the edit page.
 * All endpoints are customer-scoped, so loading is skipped when `enabled` is
 * false (admin/reseller views render campaigns without the form). */
export default function useCampaignFormOptions(enabled = true) {
  const [data, setData] = useState(EMPTY);
  const [optionsError, setOptionsError] = useState({});
  const [loading, setLoading] = useState(!!enabled);

  const load = useCallback(async () => {
    if (!enabled) { setData(EMPTY); setLoading(false); return; }
    setLoading(true);
    const errs = {};
    try {
      const [srv, tpl, ig, vend, lists, ugs, teamUsers, custSettings] = await Promise.all([
        api.get("/servers/assigned"),
        api.get("/templates"),
        api.get("/customer-routing/inbound-groups").catch((e) => {
          errs.inboundGroups = formatApiError(e.response?.data?.detail) || "Failed to load inbound groups";
          return { data: [] };
        }),
        api.get("/customer-routing/vendors"),
        api.get("/customer-leads/lists"),
        api.get("/customer-team/groups"),
        api.get("/customer-team/users").catch(() => ({ data: [] })),
        api.get("/customer-settings").catch(() => ({ data: {} })),
      ]);
      const media = srv.data.media || [];
      setData({
        assigned: srv.data.opensips || [],
        mediaServers: media,
        vicidial: srv.data.vicidial || [],
        pdServers: srv.data.pd || [],
        defaultOpensipsId: srv.data.default_opensips_id || null,
        defaultMediaId: media.find((s) => s.is_default)?.id || media[0]?.id || null,
        defaultVicidialId: srv.data.default_vicidial_id || null,
        templates: tpl.data || [],
        inboundGroups: ig.data || [],
        vendors: (vend.data || []).filter((v) => v.status === "active"),
        // Inbound is a system list — never assignable to campaigns.
        leadLists: (lists.data || []).filter(
          (l) => !l.deleted_at && l.campaign_assignable !== false && l.list_kind !== "inbound",
        ),
        userGroups: Array.isArray(ugs?.data) ? ugs.data : (ugs?.data?.groups || []),
        teamUsers: Array.isArray(teamUsers?.data) ? teamUsers.data : [],
        timezones: custSettings?.data?.timezones || [],
      });
      setOptionsError(errs);
    } finally { setLoading(false); }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const {
    pdServers, vicidial, assigned, mediaServers, inboundGroups, userGroups, teamUsers,
    leadLists, vendors, timezones,
  } = data;
  return {
    ...data,
    // Shape CampaignFormFields / wizards expect.
    options: {
      pdServers, vicidial, assigned, mediaServers, inboundGroups, userGroups, teamUsers,
      leadLists, vendors, timezones,
    },
    optionsError,
    loading,
    reload: load,
  };
}
